import { getNextApiRoute, logSentryError, SentryAcpSignInErrors as SentryErrors } from '@libs/common-next';
import type { CartUI, CartValidationResponse } from '@libs/common-node';
import { apiFetch, HttpStatus } from '@libs/core';

import {
    isCartMergeValidationFailureResponse,
    type RecoverDiscontinuedFromCartMergeValidationArgs,
    recoverDiscontinuedItemsFromCartMergeValidation,
} from './recoverDiscontinuedItemsFromCartMergeValidation';

export type ValidateCartMergeArgs = {
    mergedCart?: CartUI | null;
    onApiFail: () => void;
    handleCartError: RecoverDiscontinuedFromCartMergeValidationArgs['handleCartError'];
    setNotifications: RecoverDiscontinuedFromCartMergeValidationArgs['setNotifications'];
    removeLineItemsFromCartState: RecoverDiscontinuedFromCartMergeValidationArgs['removeLineItemsFromCartState'];
    refetchCart: RecoverDiscontinuedFromCartMergeValidationArgs['refetchCart'];
    clearStockErrors: RecoverDiscontinuedFromCartMergeValidationArgs['clearStockErrors'];
    clearErrors: RecoverDiscontinuedFromCartMergeValidationArgs['clearErrors'];
};

export async function validateCartMerge({
    mergedCart,
    onApiFail,
    handleCartError,
    setNotifications,
    removeLineItemsFromCartState,
    refetchCart,
    clearStockErrors,
    clearErrors,
}: ValidateCartMergeArgs): Promise<boolean> {
    if (!mergedCart?.id) {
        return false;
    }

    try {
        const result = await apiFetch<CartValidationResponse>(
            getNextApiRoute(`carts/${mergedCart.id}/validateCartMerge`),
            { method: 'PUT', additionalSuccessCodes: [HttpStatus.UNPROCESSABLE_ENTITY] }
        );

        if (result == null) {
            return false;
        }

        if (result instanceof Error || ('status' in result && result.status === HttpStatus.INTERNAL_SERVER_ERROR)) {
            onApiFail();
            return false;
        }

        if (isCartMergeValidationFailureResponse(result)) {
            await recoverDiscontinuedItemsFromCartMergeValidation({
                validationResponse: result,
                mergedCart,
                handleCartError,
                setNotifications,
                removeLineItemsFromCartState,
                refetchCart,
                clearStockErrors,
                clearErrors,
            });
            return false;
        }

        return 'id' in result || ('size' in result && 'timeout' in result);
    } catch (error) {
        logSentryError(error, SentryErrors.SignInForm.ValidateCartMerge);
        return false;
    }
}
